/**
 * @fileoverview Section "Empty states" — patrons d'écrans vides GNPC.
 *
 * Rappel invariant (guide frontend §4.3) : une recherche qui ne renvoie
 * aucun résultat n'est **pas une erreur**. Le ton doit rester positif
 * ("aucune contravention à payer", pas "erreur / pas trouvé").
 *
 * 3 patrons standardisés :
 *   - **Résultat vide légitime** — plaque sans contravention, historique
 *     vide, filtre trop restrictif : ton positif, pas d'icône alerte.
 *   - **Ressource indisponible** — 503 STI, service dégradé : ton calme,
 *     invite à réessayer, jamais « erreur système ».
 *   - **En attente d'action** — file vide car aucune tâche : ton neutre.
 */

import { Button, EmptyState } from '@gnpc/ui';
import { CircleCheckBig, Clock, InboxIcon, ServerCog } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionEmptyStates(): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="border-quipux-orange-200 bg-quipux-orange-50 text-quipux-navy-900 rounded-lg border p-4 text-xs">
        <span className="text-quipux-orange-700 font-semibold">Invariant :</span> un état vide n'est
        PAS une erreur. Jamais de « rien trouvé », « échec », « non disponible » pour un résultat
        légitimement vide (plaque sans contravention, historique neuf).
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DsPreview label="SearchResultsEmpty — plaque sans contravention (invariant)">
          <EmptyState
            icon={<CircleCheckBig />}
            title="Aucune contravention à payer"
            description="Bonne nouvelle : la plaque AB 572 GG ne présente aucune amende impayée."
            tone="positive"
          />
        </DsPreview>

        <DsPreview label="StiUnavailableNotice — 503 STI, ton calme">
          <EmptyState
            icon={<ServerCog />}
            title="Service temporairement indisponible"
            description="Le service de consultation est momentanément inaccessible. Merci de réessayer dans quelques instants."
            action={<Button size="sm">Réessayer</Button>}
            tone="info"
          />
        </DsPreview>

        <DsPreview label="HistoryEmpty — aucun paiement encore effectué">
          <EmptyState
            icon={<InboxIcon />}
            title="Aucun paiement enregistré"
            description="Vos paiements apparaîtront ici après leur validation."
            tone="neutral"
          />
        </DsPreview>

        <DsPreview label="RefundsQueueEmpty — aucun remboursement en attente">
          <EmptyState
            icon={<Clock />}
            title="File vide"
            description="Aucune demande de remboursement à traiter pour le moment."
            tone="neutral"
          />
        </DsPreview>
      </div>
    </div>
  );
}
